import { useCallback, useEffect, useRef, useState } from 'react';

import { INVALID_SIZE_MESSAGE, INVALID_TYPE_MESSAGE } from './constants';
import {
  FileKind,
  getFileKind,
  getUploadFileValidation,
  isImageFile,
} from './fileUtils';

interface UseFileUploadResult {
  selectedFile: File | null;
  previewUrl: string;
  kind: FileKind | null;
  error: string;
  isDragging: boolean;
  handleFileSelect: (file: File) => void;
  handleRemoveFile: () => void;
  setError: (message: string) => void;
  dismissError: () => void;
}

export const useFileUpload = (): UseFileUploadResult => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [kind, setKind] = useState<FileKind | null>(null);
  const [error, setErrorState] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const latestFileRef = useRef<File | null>(null);

  const handleFileSelect = useCallback((file: File) => {
    const { isValidFile, isValidSize } = getUploadFileValidation(file);
    if (!isValidFile) {
      setErrorState(INVALID_TYPE_MESSAGE);
      return;
    }
    if (!isValidSize) {
      setErrorState(INVALID_SIZE_MESSAGE);
      return;
    }

    latestFileRef.current = file;
    setSelectedFile(file);
    setKind(getFileKind(file));
    setErrorState('');

    if (isImageFile(file)) {
      const reader = new FileReader();
      reader.onload = () => {
        if (
          typeof reader.result === 'string' &&
          latestFileRef.current === file
        ) {
          setPreviewUrl(reader.result);
        }
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl('');
    }
  }, []);

  const handleRemoveFile = useCallback(() => {
    latestFileRef.current = null;
    setSelectedFile(null);
    setPreviewUrl('');
    setKind(null);
  }, []);

  const setError = useCallback((message: string) => {
    setErrorState(message);
  }, []);

  const dismissError = useCallback(() => {
    setErrorState('');
  }, []);

  // Drag-and-drop listeners at document level: the overlay covers the whole
  // viewport so the user can drop a file anywhere on the chat page.
  useEffect(() => {
    let dragCounter = 0;

    const handleDragEnter = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) {
        return;
      }
      e.preventDefault();
      dragCounter += 1;
      setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounter -= 1;
      if (dragCounter <= 0) {
        dragCounter = 0;
        setIsDragging(false);
      }
    };

    const handleDragOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) {
        return;
      }
      e.preventDefault();
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounter = 0;
      setIsDragging(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    };

    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDrop);

    return () => {
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDrop);
    };
  }, [handleFileSelect]);

  return {
    selectedFile,
    previewUrl,
    kind,
    error,
    isDragging,
    handleFileSelect,
    handleRemoveFile,
    setError,
    dismissError,
  };
};
